import { LanguagesEnum } from '@/api/enums/languages.enum';
import { Course } from '@/client/courses';
import { useAppSelector } from '@/hooks/store';
import hardcodedTranslations from '@/locales/hardcodedTranslations';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { DoughnutChart } from '@/ui/charts/doughnut-chart';
import { FlexContainer } from '@/ui/styled-ui';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledQuizEnd = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--big-padding);
  flex: 1;
`;

const StyledScoreChart = styled.div`
  display: contents;
`;

const StyledHeading = styled.h2`
  font-size: var(--heading-large-font-size);
  line-height: var(--heading-large-line-height);
  margin-bottom: 0;
  text-align: center;

  @media screen and (max-width: 767px) {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
  }
`;

type QuizEndProps = {
  course: Course;
  score: number;
  isDisabled?: boolean;
  attemptLanguage: LanguagesEnum;
  rtl?: boolean;
  onRetry?: () => void;
  onComplete?: () => void;
  onFinish?: () => void;
};

export const QuizEnd: React.FC<QuizEndProps> = ({
  course,
  score,
  isDisabled,
  attemptLanguage,
  rtl,
  onRetry,
  onComplete,
  onFinish,
}) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const minScore = currentUser?.account?.courseMinimumScore || 0;
  const hasPassed =
    score >= minScore || course?.exemptFromMinimumRequirementScore;

  useEffect(() => {
    if (hasPassed) {
      onComplete?.();
    }
  }, []);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <StyledQuizEnd dir={rtl ? 'rtl' : 'ltr'}>
      <StyledHeading ref={headingRef} tabIndex={-1}>
        {hasPassed
          ? hardcodedTranslations.messages.almostDone[attemptLanguage]
          : hardcodedTranslations.messages.tryAgain[attemptLanguage]}
      </StyledHeading>
      <StyledScoreChart aria-hidden={hasPassed || undefined}>
        <DoughnutChart
          id="score"
          width="240"
          cutout="70%"
          isPercentCount
          singleCount={score}
          allCount={100}
        />
      </StyledScoreChart>
      <div className="text-center">
        {hasPassed ? (
          <p>
            {hardcodedTranslations.messages.passedQuiz[attemptLanguage](score)}
          </p>
        ) : (
          hardcodedTranslations.messages.failedQuiz[attemptLanguage](minScore)
        )}
      </div>
      <FlexContainer
        gap={16}
        style={{ width: '100%', flexDirection: rtl ? 'row-reverse' : 'row' }}
        dir="ltr"
      >
        <AppButton
          label={hardcodedTranslations.buttons.retry[attemptLanguage]}
          icon="pi pi-replay"
          rtl={rtl}
          type="outlined"
          isDisabled={isDisabled}
          onClick={onRetry}
        />
        <AppButton
          label={hardcodedTranslations.buttons.finish[attemptLanguage]}
          severity="success"
          icon={`pi ${rtl ? 'pi-arrow-left' : 'pi-arrow-right'}`}
          iconPos="right"
          rtl={rtl}
          isDisabled={isDisabled || !hasPassed}
          onClick={onFinish}
        />
      </FlexContainer>
    </StyledQuizEnd>
  );
};
